import { useState, type SyntheticEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Thumbnail } from '../Thumbnail';
import { cn } from '@/lib/utils';

type ThumbnailCarouselProps = {
    imageUrls: string[];
    imageUsesAuth?: boolean;
    className?: string;
    previousImageAriaLabel?: string;
    nextImageAriaLabel?: string;
};

function stopEventCardClick(event: SyntheticEvent) {
    event.stopPropagation();
}

const slideTransition = { duration: 0.32, ease: [0.32, 0, 0.2, 1] as const };

const slideVariants = {
    enter: (direction: number) => ({
        x: direction > 0 ? '100%' : '-100%',
    }),
    center: {
        x: 0,
    },
    exit: (direction: number) => ({
        x: direction < 0 ? '100%' : '-100%',
    }),
};

const BOTTOM_FADE =
    'bg-[linear-gradient(to_top,rgba(17,18,20,0.6)_0%,rgba(17,18,20,0.25)_32%,rgba(17,18,20,0.07)_65%,transparent_100%)]';

export function ThumbnailCarousel({
    imageUrls,
    imageUsesAuth = true,
    className,
    previousImageAriaLabel = 'Previous image',
    nextImageAriaLabel = 'Next image',
}: ThumbnailCarouselProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState<1 | -1>(1);
    const shouldReduceMotion = useReducedMotion();
    const count = imageUrls.length;

    if (count === 0) {
        return (
            <div className={cn('relative h-full w-full', className)}>
                <Thumbnail size="fill" imageUsesAuth={imageUsesAuth} />
            </div>
        );
    }

    const imageSrc = imageUrls[activeIndex] ?? imageUrls[0]!;

    if (count === 1) {
        return (
            <div className={cn('relative h-full w-full', className)}>
                <Thumbnail
                    imageSrc={imageSrc}
                    size="fill"
                    imageUsesAuth={imageUsesAuth}
                />
            </div>
        );
    }

    const goToPrevious = () => {
        setSlideDirection(-1);
        setActiveIndex((previous) => (previous - 1 + count) % count);
    };
    const goToNext = () => {
        setSlideDirection(1);
        setActiveIndex((previous) => (previous + 1) % count);
    };

    const transition = shouldReduceMotion ? { duration: 0 } : slideTransition;

    const navButtonClassName =
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/80 text-grey-900 shadow-sm transition-colors duration-200 hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-grey-400';
    const navButtonInteractivityClassName =
        'pointer-events-none group-hover:pointer-events-auto';

    return (
        <div
            className={cn('group relative h-full w-full', className)}
            role="region"
            aria-roledescription="carousel"
        >
            <div
                className="relative h-full w-full overflow-hidden"
                aria-hidden={false}
            >
                <AnimatePresence initial={false} custom={slideDirection}>
                    <motion.div
                        key={activeIndex}
                        className="absolute inset-0 h-full w-full"
                        custom={slideDirection}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={transition}
                    >
                        <Thumbnail
                            imageSrc={imageSrc}
                            size="fill"
                            imageUsesAuth={imageUsesAuth}
                        />
                    </motion.div>
                </AnimatePresence>
            </div>

            <div
                className={cn(
                    'pointer-events-none absolute inset-x-0 bottom-0 z-20 h-12',
                    BOTTOM_FADE,
                )}
                aria-hidden
            />

            <div className="pointer-events-none absolute inset-0 z-[22] flex items-center justify-between px-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <button
                    type="button"
                    onClick={(event) => {
                        stopEventCardClick(event);
                        goToPrevious();
                    }}
                    onPointerDown={stopEventCardClick}
                    className={cn(
                        navButtonClassName,
                        navButtonInteractivityClassName,
                    )}
                    aria-label={previousImageAriaLabel}
                >
                    <CaretLeftIcon
                        size={16}
                        weight="bold"
                        className="shrink-0"
                    />
                </button>
                <button
                    type="button"
                    onClick={(event) => {
                        stopEventCardClick(event);
                        goToNext();
                    }}
                    onPointerDown={stopEventCardClick}
                    className={cn(
                        navButtonClassName,
                        navButtonInteractivityClassName,
                    )}
                    aria-label={nextImageAriaLabel}
                >
                    <CaretRightIcon
                        size={16}
                        weight="bold"
                        className="shrink-0"
                    />
                </button>
            </div>

            <div
                className="pointer-events-none absolute bottom-2 left-0 right-0 z-[25] flex justify-center gap-1.5"
                aria-hidden
            >
                {imageUrls.map((_, index) => (
                    <span
                        key={index}
                        className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            index === activeIndex
                                ? 'bg-white-100'
                                : 'bg-white/50',
                        )}
                    />
                ))}
            </div>
        </div>
    );
}
